import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL, getHeaders } from "../config";
import { Card, EmptyState } from "./ui";
import { Check, Quiz, Slides, Video as VideoIcon } from "./icons";

/**
 * "Review" — the learner's own record for this lesson.
 *
 * Before this existed, finishing something turned the button label to "Review"
 * and then handed back the same untouched content — the one thing the learner
 * demonstrably no longer needed. What they want at that moment is the record:
 * what did I score, how many goes did it take, when did I do it.
 *
 * Read-only and derived entirely from what the server already recorded. Nothing
 * here can grant, alter or re-report completion; it only shows what happened.
 */

const fmtDate = (value) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

/**
 * Seconds → "45s" / "12m 30s" / "1h 45m".
 *
 * Lessons here run to 100+ minutes, and the old version topped out at minutes,
 * so a 105-minute video read "105m 6s" instead of "1h 45m".
 */
const fmtMins = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  if (total <= 0) return "0m";
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

/**
 * Time is quoted only when it was actually measured.
 *
 * Progress recorded before watch/dwell tracking existed carries a real
 * `percent` but `dwellSeconds: 0` — the great majority of the rows in practice.
 * Rendering that as "0m watched" beside "78%" states something the server never
 * observed, and reads as a bug in the tracking rather than a gap in old data.
 * Where there is no measurement, say so instead of printing a zero.
 */
const videoSub = (v) => {
  if (v.watchedSeconds > 0) return `${fmtMins(v.watchedSeconds)} watched`;
  if (v.percent > 0) return "Watch time was not recorded";
  return "Not started yet";
};

const readingSub = (reading) => {
  const spent = reading.dwellSeconds > 0 ? `${fmtMins(reading.dwellSeconds)} spent` : null;
  if (reading.pageCount > 0) {
    const pages = `${reading.pagesSeen} of ${reading.pageCount} pages`;
    return spent ? `${pages} · ${spent}` : pages;
  }
  return spent || "Time spent was not recorded";
};

/** Red under 50, amber under 75, green above — the app's usual score scale. */
const scoreTone = (percent) =>
  percent >= 75
    ? { bg: "#D1FAE5", fg: "#065F46" }
    : percent >= 50
    ? { bg: "#FEF3C7", fg: "#92400E" }
    : { bg: "#FEE2E2", fg: "#991B1B" };

const ScorePill = ({ percent }) => {
  const tone = scoreTone(percent);
  return (
    <span
      style={{
        background: tone.bg,
        color: tone.fg,
        fontWeight: 700,
        fontSize: 12,
        padding: "2px 9px",
        borderRadius: 20,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {percent}%
    </span>
  );
};

const Row = ({ icon: Icon, title, sub, right, last }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "11px 0",
      // The last row's divider would sit right on the card's own bottom edge,
      // reading as a doubled line.
      borderBottom: last ? "none" : "1px solid var(--line, #eef0f3)",
    }}
  >
    {Icon && (
      <span style={{ color: "var(--muted-ink, #6b7280)", flexShrink: 0 }}>
        <Icon size={18} />
      </span>
    )}
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
      {sub && (
        <div style={{ fontSize: 12, color: "var(--muted-ink, #6b7280)", marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
    {right && <div style={{ flexShrink: 0 }}>{right}</div>}
  </div>
);

export const ReviewSection = ({ pptItemId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!pptItemId) return;
    let live = true;
    setLoading(true);
    (async () => {
      try {
        const { data: res } = await axios.get(`${API_URL}/api/progress/review`, {
          params: { pptItemId },
          headers: getHeaders().headers,
        });
        if (live) setData(res?.data || null);
      } catch (e) {
        if (live) setData(null);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [pptItemId]);

  if (loading) {
    return (
      <Card>
        <EmptyState icon={Quiz} text="Loading your record…" />
      </Card>
    );
  }

  if (!data?.hasHistory) {
    return (
      <Card>
        <EmptyState
          icon={Quiz}
          text="Nothing to review yet — your scores and attempts will appear here once you have worked through this lesson."
        />
      </Card>
    );
  }

  const { reading, videos = [], assessments = [] } = data;

  return (
    // `.ds-stage` (the section frame this renders into) carries no padding of
    // its own — VideoSection insets itself the same way. Without this the cards
    // ran edge to edge and `overflow: hidden` on the stage sheared off their
    // right border, which is what made the score column look cut off.
    <div className="ds-stack ds-gap-4" style={{ padding: "var(--s-5)" }}>
      {assessments.map((a) => (
        // `pad` is not the default on Card: without it the content sits flush
        // against the border, which is why the score column read as clipped.
        <Card key={a._id} pad>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <h3 className="ds-h3" style={{ margin: 0 }}>
              {a.name}
            </h3>
            <span className="ds-sub" style={{ margin: 0 }}>
              {a.questionCount} question{a.questionCount === 1 ? "" : "s"} ·{" "}
              {a.totalMarks} marks
            </span>
          </div>

          {a.attempts.length === 0 ? (
            <p className="ds-sub" style={{ margin: 0 }}>
              You have not attempted this yet.
            </p>
          ) : (
            <>
              {/* Best first, because that is the one that counts. */}
              <div
                style={{
                  display: "flex",
                  gap: 22,
                  flexWrap: "wrap",
                  padding: "10px 0 14px",
                }}
              >
                <div>
                  <div className="ds-sub" style={{ margin: 0 }}>
                    Best score
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>
                    {a.best.marks} / {a.best.outOf}{" "}
                    <ScorePill percent={a.best.percent} />
                  </div>
                </div>
                <div>
                  <div className="ds-sub" style={{ margin: 0 }}>
                    Attempts
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>
                    {a.attempts.length}
                  </div>
                </div>
                <div>
                  <div className="ds-sub" style={{ margin: 0 }}>
                    Last attempt
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginTop: 6 }}>
                    {fmtDate(a.latest.submittedAt)}
                  </div>
                </div>
              </div>

              {/* Oldest first, so improvement over attempts reads left to right. */}
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13.5,
                    minWidth: 340,
                  }}
                >
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--muted-ink, #6b7280)" }}>
                      <th style={{ padding: "6px 8px 6px 0", fontWeight: 600 }}>
                        Attempt
                      </th>
                      <th style={{ padding: "6px 8px", fontWeight: 600 }}>Date</th>
                      <th style={{ padding: "6px 8px", fontWeight: 600, textAlign: "right" }}>
                        Marks
                      </th>
                      <th style={{ padding: "6px 0 6px 8px", fontWeight: 600, textAlign: "right" }}>
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.attempts.map((at) => {
                      const isBest = at.attempt === a.best.attempt;
                      return (
                        <tr
                          key={at.attempt}
                          style={{ borderTop: "1px solid var(--line, #eef0f3)" }}
                        >
                          <td style={{ padding: "8px 8px 8px 0" }}>
                            #{at.attempt}
                            {isBest && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#065F46",
                                }}
                              >
                                best
                              </span>
                            )}
                          </td>
                          <td style={{ padding: "8px" }}>{fmtDate(at.submittedAt)}</td>
                          <td
                            style={{
                              padding: "8px",
                              textAlign: "right",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {at.marks} / {at.outOf}
                          </td>
                          <td style={{ padding: "8px 0 8px 8px", textAlign: "right" }}>
                            <ScorePill percent={at.percent} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      ))}

      {(reading || videos.length > 0) && (
        <Card pad>
          <h3 className="ds-h3" style={{ margin: "0 0 4px" }}>
            Your activity
          </h3>
          <p className="ds-sub" style={{ marginTop: 0 }}>
            What this lesson has recorded for you.
          </p>

          {reading && (
            <Row
              icon={Slides}
              title="Slides &amp; document"
              sub={readingSub(reading)}
              last={videos.length === 0}
              right={
                reading.completed ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: "#D1FAE5",
                      color: "#065F46",
                      fontWeight: 700,
                      fontSize: 12,
                      padding: "3px 10px",
                      borderRadius: 20,
                    }}
                  >
                    <Check size={12} /> {fmtDate(reading.completedAt)}
                  </span>
                ) : (
                  <span className="ds-sub" style={{ margin: 0 }}>
                    {reading.percent}%
                  </span>
                )
              }
            />
          )}

          {videos.map((v, i) => (
            <Row
              key={v._id}
              icon={VideoIcon}
              title={v.name}
              sub={videoSub(v)}
              last={i === videos.length - 1}
              right={
                v.completed ? (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: "#D1FAE5",
                      color: "#065F46",
                      fontWeight: 700,
                      fontSize: 12,
                      padding: "3px 10px",
                      borderRadius: 20,
                    }}
                  >
                    <Check size={12} /> {fmtDate(v.completedAt)}
                  </span>
                ) : (
                  <span className="ds-sub" style={{ margin: 0 }}>
                    {v.percent}%
                  </span>
                )
              }
            />
          ))}
        </Card>
      )}
    </div>
  );
};

export default ReviewSection;
